import { ParserBaseRuleType } from '../../parser/ParserTypes';
import { ConfigurationItem } from '../dependencies/DependenciesTypes';

const RT_NAME_FUNNEL_ID_SEPARATOR = '?$%=';
const LFN_RT_NAME_OBJECTNAME_SEPARATOR = '.';
export const FULL_NAME_SEPARATOR = '%&?';
export const MASTER_KEY = '__MASTER__';

interface CreateRecordTypeIdentifierProps {
  recordTypeName: string;
  funnelId: string;
  objectName: string;
  leadingField: string;
}

interface CreateStepIdProps extends CreateRecordTypeIdentifierProps {
  stepFullName: string;
}

export const createRecordTypeIdentifier = ({
  recordTypeName,
  funnelId,
  objectName,
  leadingField,
}: CreateRecordTypeIdentifierProps): string => {
  // If record type name is not provided, that means that funnel was not yet deployed
  // to properly support sweep elements that may use this record type we should provide funnelId
  // to properly support SF elements that may use this record type we should provide MASTER_KEY
  return `${leadingField}${LFN_RT_NAME_OBJECTNAME_SEPARATOR}${recordTypeName ?? MASTER_KEY}${RT_NAME_FUNNEL_ID_SEPARATOR}${funnelId}${LFN_RT_NAME_OBJECTNAME_SEPARATOR}${objectName}`;
};

export const getDataFromRecordTypeIdenifier = (
  recordTypeIdenifier: string,
): {
  leadingField: string;
  objectName: string;
  recordTypeName: string;
  funnelId: string | undefined;
  recordTypeNameAndFunnelId: string;
} => {
  const [leadingField, recordTypeNameAndFunnelId, objectName] = recordTypeIdenifier.split(
    LFN_RT_NAME_OBJECTNAME_SEPARATOR,
  );
  const [recordTypeName, funnelId] = recordTypeNameAndFunnelId.split(RT_NAME_FUNNEL_ID_SEPARATOR);
  return { leadingField, objectName, recordTypeName, funnelId, recordTypeNameAndFunnelId };
};

export const createStepId = ({
  recordTypeName,
  objectName,
  stepFullName,
  funnelId,
  leadingField,
}: CreateStepIdProps): string => {
  const rtIdentifier = createRecordTypeIdentifier({
    recordTypeName,
    objectName,
    funnelId,
    leadingField,
  });
  return `${rtIdentifier}${FULL_NAME_SEPARATOR}${stepFullName}`;
};

export const getDataFromStepId = (stepId: string) => {
  const [rtIdentifier, stepFullName] = stepId.split(FULL_NAME_SEPARATOR);
  return { rtIdentifier, stepFullName };
};

//Expecting id created with createRecordTypeIdentifier
export const isConfigurationItemUsedByRecordType = (id: string) => (rule: ConfigurationItem) => {
  const { recordTypeName, objectName, leadingField, funnelId } = getDataFromRecordTypeIdenifier(id);
  const idOfFunnelLevel = createRecordTypeIdentifier({
    objectName,
    leadingField,
    funnelId: funnelId ?? '',
    recordTypeName: '',
  });

  const idOfRecordTypeLevel = createRecordTypeIdentifier({
    objectName,
    leadingField,
    recordTypeName,
    funnelId: '',
  });

  const isObjectLevel = !rule.usedByRecordTypes?.length;
  const isLayout = rule.type === ParserBaseRuleType.layouts;

  if (isObjectLevel && !isLayout) {
    return true;
  }

  return rule.usedByRecordTypes?.some((ruleRecordTypeIdenifier) => {
    const { recordTypeName, objectName, leadingField, funnelId } =
      getDataFromRecordTypeIdenifier(ruleRecordTypeIdenifier);

    const funnelLevel = createRecordTypeIdentifier({
      objectName,
      leadingField,
      funnelId: funnelId ?? '',
      recordTypeName: '',
    });

    const isFunnelLevelMatch = funnelId ? idOfFunnelLevel.endsWith(funnelLevel) : false;

    const recordTypeLevel = createRecordTypeIdentifier({
      objectName,
      leadingField,
      recordTypeName,
      funnelId: '',
    });

    const isRecordTypeLevelMatch = recordTypeName
      ? idOfRecordTypeLevel.endsWith(recordTypeLevel)
      : false;

    return isFunnelLevelMatch || isRecordTypeLevelMatch;
  });
};

export const isConfigurationItemUsedByStep = (id: string) => (rule: ConfigurationItem) => {
  const { stepFullName, rtIdentifier } = getDataFromStepId(id);
  const isFromRt = isConfigurationItemUsedByRecordType(rtIdentifier)(rule);
  return (
    isFromRt &&
    rule.stagesNames?.some((name) => getDataFromStepId(name).stepFullName === stepFullName)
  );
};
